import { Amplify } from 'aws-amplify';
import {
  signIn, signOut,
  getCurrentUser, resetPassword, confirmResetPassword,
  signUp, confirmSignUp, resendSignUpCode,
} from 'aws-amplify/auth';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Hub } from 'aws-amplify/utils';
import awsConfig from './aws-exports';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Topbar from './components/Topbar';
import React, { useEffect, useState, Suspense } from 'react';
import { apiGet } from './services/api';
// eslint-disable-next-line no-unused-vars
import { clearLocalStorageIfUserChanged } from './services/videoHistory';
import { getOrUpdateStreak } from './services/activityTracking';
import ErrorBoundary from './components/ErrorBoundary';

Amplify.configure(awsConfig);

/* ── Session expiry ─────────────────────────────────────────
   Force sign-out after this many days even if Cognito refresh
   token is still valid (Cognito default is 30 days).
   Change SESSION_EXPIRY_DAYS to whatever you prefer.
──────────────────────────────────────────────────────────── */
const SESSION_EXPIRY_DAYS = 7;
const SESSION_TS_KEY = 'ts_login_at';

const Dashboard          = React.lazy(() => import('./pages/Dashboard'));
const MyLearning         = React.lazy(() => import('./pages/MyLearning'));
const Tasks              = React.lazy(() => import('./pages/Tasks'));
const InterviewQuestions       = React.lazy(() => import('./pages/InterviewQuestions'));
const InterviewQuestionsDetail = React.lazy(() => import('./pages/InterviewQuestionsDetail'));
const MockExam           = React.lazy(() => import('./pages/MockExam'));
const Admin              = React.lazy(() => import('./pages/Admin'));
const Profile            = React.lazy(() => import('./pages/Profile'));
const Simulations        = React.lazy(() => import('./pages/Simulations'));
const SimulationTopic    = React.lazy(() => import('./pages/SimulationTopic'));
const SimulationDetail   = React.lazy(() => import('./pages/SimulationDetail'));
const AIInterview        = React.lazy(() => import('./pages/AIInterview'));
const InterviewHistory   = React.lazy(() => import('./pages/InterviewHistory'));
const ResumePrep         = React.lazy(() => import('./pages/ResumePrep'));

const PageLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 'calc(100vh - 56px)', background: '#0B1120',
    gap: '12px', fontFamily: 'Inter, system-ui, sans-serif',
  }}>
    <div style={{
      width: '32px', height: '32px', border: '3px solid rgba(255,255,255,0.06)',
      borderTop: '3px solid #22D3EE', borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
    }} />
    <span style={{ color: '#64748B', fontSize: '0.85rem' }}>Loading…</span>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/* ── Auth Input ─────────────────────────────── */
function AuthInput({ label, type = 'text', value, onChange, placeholder, autoComplete }) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className="auth-field">
      <label className="auth-label">{label}</label>
      <div className="auth-input-wrap">
        <input
          className="auth-input"
          type={isPassword && show ? 'text' : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        {isPassword && (
          <button
            type="button"
            className="auth-eye-btn"
            onClick={() => setShow(s => !s)}
            tabIndex={-1}
          >
            {show ? '🙈' : '👁'}
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Login Page ─────────────────────────────── */
function LoginPage({ onSignIn }) {
  const [view, setView]       = useState('signIn');   // signIn | signUp | confirmSignUp | forgotPwd | confirmPwd
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');

  // Sign In
  const [siEmail, setSiEmail]   = useState('');
  const [siPwd, setSiPwd]       = useState('');

  // Sign Up
  const [suFirst, setSuFirst]       = useState('');
  const [suLast, setSuLast]         = useState('');
  const [suEmail, setSuEmail]       = useState('');
  const [suPhone, setSuPhone]       = useState('');
  const [suCity, setSuCity]         = useState('');
  const [suCountry, setSuCountry]   = useState('');
  const [suPwd, setSuPwd]           = useState('');
  const [suConfirm, setSuConfirm]   = useState('');

  // Confirm
  const [confEmail, setConfEmail]   = useState('');
  const [confCode, setConfCode]     = useState('');

  // Forgot
  const [fpEmail, setFpEmail]   = useState('');
  const [fpCode, setFpCode]     = useState('');
  const [fpNewPwd, setFpNewPwd] = useState('');

  const err = (msg) => { setError(msg); setLoading(false); };

  /* Redirect to the verification screen with a fresh code (used when sign-in
     hits an unconfirmed account, whether Amplify reports it via nextStep or
     by throwing UserNotConfirmedException). */
  const redirectToVerify = async (email) => {
    setConfEmail(email);
    setView('confirm');
    setError('');
    try {
      await resendSignUpCode({ username: email });
      setInfo('Your account isn’t verified yet. We’ve sent a new code to your email — please check your spam/junk folder too if you don’t see it.');
    } catch {
      setInfo('Your account isn’t verified yet. Enter the code sent to your email, or tap "Resend code" below — remember to check your spam/junk folder too.');
    }
    setLoading(false);
  };

  /* Sign In */
  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!siEmail || !siPwd) return err('Please fill in all fields.');
    setLoading(true); setError('');
    const email = siEmail.trim().toLowerCase();
    try {
      const result = await signIn({ username: email, password: siPwd });

      if (!result.isSignedIn && result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        return redirectToVerify(email);
      }

      const user = await getCurrentUser();
      localStorage.setItem(SESSION_TS_KEY, Date.now().toString());
      onSignIn(user);
    } catch (ex) {
      if (ex.name === 'UserNotConfirmedException') {
        return redirectToVerify(email);
      }
      err(ex.message || 'Sign in failed.');
    }
  };

  /* Forgot Password */
  const handleForgotPwd = async (e) => {
    e.preventDefault();
    if (!fpEmail) return err('Please enter your email.');
    setLoading(true); setError('');
    try {
      await resetPassword({ username: fpEmail.trim().toLowerCase() });
      setView('confirmPwd');
      setInfo('Reset code sent to your email.');
      setLoading(false);
    } catch (ex) {
      err(ex.message || 'Failed to send reset code.');
    }
  };

  /* Confirm New Password */
  const handleConfirmPwd = async (e) => {
    e.preventDefault();
    if (!fpCode || !fpNewPwd) return err('Please fill in all fields.');
    setLoading(true); setError('');
    try {
      await confirmResetPassword({
        username: fpEmail.trim().toLowerCase(),
        confirmationCode: fpCode,
        newPassword: fpNewPwd,
      });
      setView('signIn');
      setInfo('Password reset! Please sign in.');
      setLoading(false);
    } catch (ex) {
      err(ex.message || 'Password reset failed.');
    }
  };

  /* Sign Up */
  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!suFirst || !suLast || !suEmail || !suPhone || !suCity || !suCountry || !suPwd || !suConfirm)
      return err('Please fill in all fields.');
    if (suPwd !== suConfirm) return err('Passwords do not match.');
    if (suPwd.length < 8) return err('Password must be at least 8 characters.');
    setLoading(true); setError('');
    try {
      await signUp({
        username: suEmail.trim().toLowerCase(),
        password: suPwd,
        options: {
          userAttributes: {
            name:              suFirst,
            family_name:       suLast,
            email:             suEmail.trim().toLowerCase(),
            phone_number:      `+${suPhone}`,
            'custom:city':     suCity,
            'custom:country':  suCountry,
          },
        },
      });
      setConfEmail(suEmail.trim().toLowerCase());
      setView('confirm');
      setInfo('A verification code has been sent to your email.');
      setLoading(false);
    } catch (ex) {
      err(ex.message || 'Sign up failed.');
    }
  };

  /* Confirm Sign Up */
  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    if (!confCode) return err('Please enter the verification code.');
    setLoading(true); setError('');
    try {
      await confirmSignUp({ username: confEmail, confirmationCode: confCode });
      setView('signIn');
      setSiEmail(confEmail);
      setInfo('Account confirmed! Please sign in.');
      setLoading(false);
    } catch (ex) {
      err(ex.message || 'Confirmation failed.');
    }
  };

  /* Resend Code */
  const handleResend = async () => {
    try {
      await resendSignUpCode({ username: confEmail });
      setInfo('Code resent to your email.');
      setError('');
    } catch (ex) {
      setError(ex.message || 'Failed to resend code.');
    }
  };

  return (
    <div className="page-wrapper">
    <div className="split-container">
      {/* ── Left Branding Panel ── */}
      <div className="left-panel">
        <div className="lp-content">

          {/* Top-left text */}
          <div className="lp-top">
            <h1 className="lp-heading">
              <span>Learn.</span> <span>Build.</span> <span>Deploy.</span>
            </h1>
            <p className="lp-tagline">Master AWS &amp; DevOps with TeraSkill Academy</p>
          </div>

          {/* ── DevOps Infinity Illustration ── */}
          <div className="lp-illustration">
            <svg viewBox="0 0 460 310" className="devops-svg" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <path id="infPath"
                  d="M 230,150 C 290,50 430,50 430,150 C 430,250 290,250 230,150 C 170,50 30,50 30,150 C 30,250 170,250 230,150 Z"
                  fill="none"/>
              </defs>

              {/* Outer glow */}
              <path d="M 230,150 C 290,50 430,50 430,150 C 430,250 290,250 230,150 C 170,50 30,50 30,150 C 30,250 170,250 230,150 Z"
                fill="none" stroke="rgba(45,212,191,0.12)" strokeWidth="32"/>

              {/* Dashed infinity track */}
              <path d="M 230,150 C 290,50 430,50 430,150 C 430,250 290,250 230,150 C 170,50 30,50 30,150 C 30,250 170,250 230,150 Z"
                fill="none" stroke="rgba(45,212,191,0.50)" strokeWidth="1.5" strokeDasharray="8 5"/>


              {/* Animated glowing dot */}
              <circle r="5" fill="#2DD4BF" opacity="0.9">
                <animateMotion dur="6s" repeatCount="indefinite">
                  <mpath href="#infPath"/>
                </animateMotion>
              </circle>
              {/* Trailing dot */}
              <circle r="3" fill="#2DD4BF" opacity="0.5">
                <animateMotion dur="6s" repeatCount="indefinite" begin="0.25s">
                  <mpath href="#infPath"/>
                </animateMotion>
              </circle>

              {/* ── 8 DevOps Stage Nodes — on-curve positions (bezier midpoints & quarter-points) ── */}
              {[
                // Left loop  (seg C: 230→30 top arc, seg D: 30→230 bottom arc)
                { label: 'Plan',    icon: '📋', color: '#60A5FA', x: 190, y: 108 },
                { label: 'Code',    icon: '💻', color: '#818CF8', x: 108, y: 75  },
                { label: 'Build',   icon: '⚙️', color: '#34D399', x: 30,  y: 150 },
                { label: 'Test',    icon: '🧪', color: '#2DD4BF', x: 108, y: 225 },
                { label: 'Release', icon: '🏷️', color: '#FBBF24', x: 352, y: 75  },
                { label: 'Deploy',  icon: '🚀', color: '#F97316', x: 430, y: 150 },
                { label: 'Operate', icon: '🖥️', color: '#F87171', x: 352, y: 225 },
                { label: 'Monitor', icon: '📊', color: '#4ADE80', x: 270, y: 192 },
              ].map(({ label, icon, color, x, y }) => (
                <g key={label}>
                  {/* Glow halo */}
                  <circle cx={x} cy={y} r="34" fill={color} opacity="0.07"/>
                  {/* Node circle */}
                  <circle cx={x} cy={y} r="26" fill="#0D1B2A" stroke={color} strokeWidth="1.8"/>
                  <circle cx={x} cy={y} r="22" fill={`${color}18`}/>
                  {/* Icon */}
                  <text x={x} y={y - 3} textAnchor="middle" fontSize="14" dominantBaseline="middle">{icon}</text>
                  {/* Label */}
                  <text x={x} y={y + 16} textAnchor="middle" fill={color} fontSize="8" fontWeight="700">{label}</text>
                </g>
              ))}
            </svg>
          </div>

        </div>
      </div>

      {/* ── Right Login Panel ── */}
      <div className="right-panel">
        {/* Mobile-only brand header */}
        <div className="mobile-brand">
          <img src="/logo.png" alt="TeraSkill Academy" className="mobile-brand-logo" />
          <h2 className="mobile-brand-title"><span>Learn.</span> Build. Deploy.</h2>
          <p className="mobile-brand-tagline">Master AWS &amp; DevOps with TeraSkill Academy</p>
        </div>

        {/* Background logo watermarks */}
        <div className="rp-bg-deco" aria-hidden="true">
          <img src="/logo.png" alt="" className="rp-logo-top" />
          <img src="/logo.png" alt="" className="rp-logo-bottom" />
        </div>

        <div className="login-card">
          {/* Logo */}
          <div className="login-card-top">
            <img src="/logo-full.jpg" alt="TeraSkill Academy" className="login-card-logo" />
          </div>

          {/* ── Sign In ── */}
          {view === 'signIn' && (
            <>
              {error && <div className="auth-error">{error}</div>}
              {info  && <div className="auth-info">{info}</div>}
              <form onSubmit={handleSignIn} className="auth-form">
                <AuthInput label="Email" type="email" value={siEmail}
                  onChange={setSiEmail} placeholder="Enter your email"
                  autoComplete="email" />
                <AuthInput label="Password" type="password" value={siPwd}
                  onChange={setSiPwd} placeholder="Enter your password"
                  autoComplete="current-password" />
                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
                <button type="button" className="auth-link-btn"
                  onClick={() => { setView('forgotPwd'); setError(''); setInfo(''); }}>
                  Forgot your password?
                </button>
                <button type="button" className="auth-link-btn"
                  onClick={() => { setView('signUp'); setError(''); setInfo(''); }}>
                  Don't have an account? Sign Up
                </button>
              </form>
            </>
          )}

          {/* ── Sign Up ── */}
          {view === 'signUp' && (
            <>
              <h2 className="auth-view-title">Create Account</h2>
              <p className="auth-view-sub">Sign up to get started with TeraSkill Academy.</p>
              {error && <div className="auth-error">{error}</div>}
              {info  && <div className="auth-info">{info}</div>}
              <form onSubmit={handleSignUp} className="auth-form">
                <div className="auth-row">
                  <AuthInput label="First Name" value={suFirst} onChange={setSuFirst}
                    placeholder="First name" autoComplete="given-name" />
                  <AuthInput label="Last Name" value={suLast} onChange={setSuLast}
                    placeholder="Last name" autoComplete="family-name" />
                </div>
                <AuthInput label="Email" type="email" value={suEmail}
                  onChange={setSuEmail} placeholder="Enter your email"
                  autoComplete="email" />
                <div className="auth-field">
                  <label className="auth-label">Mobile Number</label>
                  <PhoneInput
                    country={'in'}
                    value={suPhone}
                    onChange={phone => setSuPhone(phone)}
                    enableSearch={true}
                    searchPlaceholder="Search country..."
                    inputClass="auth-phone-lib-input"
                    containerClass="auth-phone-lib-container"
                    buttonClass="auth-phone-lib-btn"
                    dropdownClass="auth-phone-lib-dropdown"
                    searchClass="auth-phone-lib-search"
                    placeholder="Enter mobile number"
                    autoComplete="tel"
                  />
                </div>
                <div className="auth-row">
                  <AuthInput label="City" value={suCity} onChange={setSuCity}
                    placeholder="Your city" />
                  <AuthInput label="Country" value={suCountry} onChange={setSuCountry}
                    placeholder="Your country" />
                </div>
                <AuthInput label="Password" type="password" value={suPwd}
                  onChange={setSuPwd} placeholder="Create a password (min 8 chars)"
                  autoComplete="new-password" />
                <AuthInput label="Confirm Password" type="password" value={suConfirm}
                  onChange={setSuConfirm} placeholder="Confirm your password"
                  autoComplete="new-password" />
                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? 'Creating account…' : 'Create Account'}
                </button>
                <button type="button" className="auth-link-btn"
                  onClick={() => { setView('signIn'); setError(''); setInfo(''); }}>
                  Already have an account? Sign In
                </button>
              </form>
            </>
          )}

          {/* ── Confirm Sign Up ── */}
          {view === 'confirm' && (
            <>
              <h2 className="auth-view-title">Verify Your Email</h2>
              <p className="auth-view-sub">Enter the 6-digit code sent to <strong>{confEmail}</strong>.</p>
              <p className="auth-view-note">Don't see it? Check your spam/junk folder too.</p>
              {error && <div className="auth-error">{error}</div>}
              {info  && <div className="auth-info">{info}</div>}
              <form onSubmit={handleConfirmSignUp} className="auth-form">
                <AuthInput label="Verification Code" value={confCode}
                  onChange={setConfCode} placeholder="Enter 6-digit code"
                  autoComplete="one-time-code" />
                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? 'Verifying…' : 'Confirm Account'}
                </button>
                <button type="button" className="auth-link-btn" onClick={handleResend}>
                  Resend code
                </button>
                <button type="button" className="auth-link-btn"
                  onClick={() => { setView('signIn'); setError(''); setInfo(''); }}>
                  Back to Sign In
                </button>
              </form>
            </>
          )}

          {/* ── Forgot Password ── */}
          {view === 'forgotPwd' && (
            <>
              <h2 className="auth-view-title">Reset Password</h2>
              <p className="auth-view-sub">Enter your email to receive a reset code.</p>
              {error && <div className="auth-error">{error}</div>}
              {info  && <div className="auth-info">{info}</div>}
              <form onSubmit={handleForgotPwd} className="auth-form">
                <AuthInput label="Email" type="email" value={fpEmail}
                  onChange={setFpEmail} placeholder="Enter your email"
                  autoComplete="email" />
                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? 'Sending…' : 'Send Reset Code'}
                </button>
                <button type="button" className="auth-link-btn"
                  onClick={() => { setView('signIn'); setError(''); setInfo(''); }}>
                  Back to Sign In
                </button>
              </form>
            </>
          )}

          {/* ── Confirm New Password ── */}
          {view === 'confirmPwd' && (
            <>
              <h2 className="auth-view-title">Set New Password</h2>
              <p className="auth-view-sub">Enter the code sent to your email and your new password.</p>
              {error && <div className="auth-error">{error}</div>}
              {info  && <div className="auth-info">{info}</div>}
              <form onSubmit={handleConfirmPwd} className="auth-form">
                <AuthInput label="Reset Code" value={fpCode}
                  onChange={setFpCode} placeholder="6-digit code"
                  autoComplete="one-time-code" />
                <AuthInput label="New Password" type="password" value={fpNewPwd}
                  onChange={setFpNewPwd} placeholder="Enter new password"
                  autoComplete="new-password" />
                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? 'Resetting…' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>

      </div>
    </div>

    {/* ── Global Footer ── spans both panels ── */}
    <footer className="global-footer">
      <a href="mailto:teraskillacademy@gmail.com" className="gf-email">
        ✉ teraskillacademy@gmail.com
      </a>
      <span className="gf-sep">|</span>
      <span className="gf-copy">© {new Date().getFullYear()} TeraSkill Academy. All rights reserved.</span>
    </footer>

    </div>
  );
}


/* ── App Shell (sidebar + topbar + content) ─────── */
function AppShell({ user, onSignOut, userName, isAdmin, studentData, streakDays }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const isCohortAssigned = Boolean(isAdmin || studentData?.cohorts?.length > 0);

  return (
    <div className="app-shell">
      <Navbar
        user={user}
        signOut={onSignOut}
        userName={userName}
        isAdmin={isAdmin}
        isCohortAssigned={isCohortAssigned}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="app-main">
        <Topbar
          userName={userName}
          streakDays={streakDays}
          onMobileMenuToggle={() => setMobileOpen(v => !v)}
          onSignOut={onSignOut}
        />
        <div className="app-content">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<ErrorBoundary><Dashboard studentData={studentData} userName={userName} /></ErrorBoundary>} />
              <Route path="/mylearning" element={<ErrorBoundary><MyLearning studentData={studentData} /></ErrorBoundary>} />
              <Route path="/tasks"      element={<ErrorBoundary><Tasks /></ErrorBoundary>} />
              <Route path="/interview"                  element={
                isCohortAssigned
                  ? <ErrorBoundary><InterviewQuestions studentData={studentData} /></ErrorBoundary>
                  : <Navigate to="/" replace />
              } />
              <Route path="/interview/ai-mock"       element={
                studentData?.is_admin
                  ? <ErrorBoundary><AIInterview studentData={studentData} /></ErrorBoundary>
                  : <Navigate to="/interview" replace />
              } />
              <Route path="/interview/history"       element={
                isCohortAssigned
                  ? <ErrorBoundary><InterviewHistory /></ErrorBoundary>
                  : <Navigate to="/" replace />
              } />
              <Route path="/interview/:topicSlug"    element={
                isCohortAssigned
                  ? <ErrorBoundary><InterviewQuestionsDetail /></ErrorBoundary>
                  : <Navigate to="/" replace />
              } />
              <Route path="/mockexam"      element={
                isCohortAssigned
                  ? <ErrorBoundary><MockExam studentData={studentData} /></ErrorBoundary>
                  : <Navigate to="/" replace />
              } />
              <Route path="/resume-prep"   element={
                isCohortAssigned
                  ? <ErrorBoundary><ResumePrep studentData={studentData} /></ErrorBoundary>
                  : <Navigate to="/" replace />
              } />
              <Route path="/admin"             element={<ErrorBoundary><Admin /></ErrorBoundary>} />
              <Route path="/admin/questions"   element={<ErrorBoundary><Admin /></ErrorBoundary>} />
              <Route path="/profile"    element={<ErrorBoundary><Profile studentData={studentData} /></ErrorBoundary>} />
              <Route path="/ai-interview" element={<ErrorBoundary><AIInterview studentData={studentData} /></ErrorBoundary>} />
              <Route path="/simulations"                          element={<ErrorBoundary><Simulations /></ErrorBoundary>} />
              <Route path="/simulations/:topicSlug"               element={<ErrorBoundary><SimulationTopic /></ErrorBoundary>} />
              <Route path="/simulations/:topicSlug/:simulationId" element={<ErrorBoundary><SimulationDetail /></ErrorBoundary>} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

/* ── Main App ───────────────────────────────── */
function MainApp({ user, onSignOut }) {
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading]         = useState(true);

  const userEmail = user?.signInDetails?.loginId || user?.username || '';
  const userName  = studentData?.first_name
    ? `${studentData.first_name} ${studentData.last_name}`
    : userEmail.split('@')[0];

  useEffect(() => {
    if (!userEmail) { setLoading(false); return; }
    apiGet('/student')
      .then(data => { setStudentData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userEmail]);

  if (loading) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', background: '#0B1120',
        gap: '16px', fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.06)',
          borderTop: '3px solid #22D3EE', borderRadius: '50%',
          animation: 'spin 0.7s linear infinite' }} />
        <p style={{ color: '#64748B', margin: 0, fontSize: '0.85rem' }}>Loading TeraSkill…</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', background: '#0B1120',
        gap: '12px', fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <div style={{ fontSize: '2.5rem' }}>⚠️</div>
        <h2 style={{ color: '#E5E7EB', margin: 0, fontWeight: 700 }}>Could not load your profile</h2>
        <p style={{ color: '#64748B', margin: 0, fontSize: '0.88rem' }}>
          Please refresh or sign out and try again.
        </p>
        <button onClick={onSignOut} style={{
          marginTop: '8px', padding: '9px 22px',
          background: 'linear-gradient(135deg, #22D3EE, #4ADE80)',
          color: '#0B1120', border: 'none', borderRadius: '8px',
          fontWeight: '700', cursor: 'pointer', fontSize: '0.88rem',
        }}>Sign Out</button>
      </div>
    );
  }

  const streakDays = studentData?.email ? getOrUpdateStreak(studentData.email) : 0;

  return (
    <BrowserRouter>
      <AppShell
        user={user}
        onSignOut={onSignOut}
        userName={userName}
        isAdmin={studentData?.is_admin}
        studentData={studentData}
        streakDays={streakDays}
      />
    </BrowserRouter>
  );
}

/* ── Root ───────────────────────────────────── */
function App() {
  const [authUser, setAuthUser]     = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then(async u => {
        // Check if session has exceeded the max age
        const loginAt = parseInt(localStorage.getItem(SESSION_TS_KEY) || '0', 10);
        const ageMs   = Date.now() - loginAt;
        const maxMs   = SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

        if (loginAt && ageMs > maxMs) {
          // Session is too old — force sign-out
          localStorage.removeItem(SESSION_TS_KEY);
          await signOut();
          setAuthUser(null);
        } else {
          setAuthUser(u);
        }
        setAuthLoading(false);
      })
      .catch(() => { setAuthUser(null); setAuthLoading(false); });

    const unsub = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedOut') setAuthUser(null);
    });
    return unsub;
  }, []);

  const handleSignOut = async () => {
    localStorage.removeItem(SESSION_TS_KEY);
    await signOut();
    setAuthUser(null);
  };

  if (authLoading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0B1120',
      }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid rgba(255,255,255,0.06)',
          borderTop: '3px solid #22D3EE', borderRadius: '50%',
          animation: 'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!authUser) return <LoginPage onSignIn={setAuthUser} />;
  return <MainApp user={authUser} onSignOut={handleSignOut} />;
}

export default App;
